'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import PhotoAnnotator, { type Annotation } from '@/app/customer-dashboard/components/PhotoAnnotator';
import type { InspectionPdfData, InspectionPdfItem } from '@/components/common/InspectionPDF';

const NAVY = '#1C2E5E';
const ORANGE = '#E89A3C';

export interface InspectionCustomer {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

interface InspectionBuilderProps {
  inspectionId: string;
  pipelineId: string;
  customer: InspectionCustomer;
  onClose: () => void;
}

interface Item {
  id: string;
  storage_path: string | null;
  source_photo_id: string | null;
  note: string;
  annotations: any[];
  sort_order: number;
  src?: string; // resolved signed URL
}

// Resolve signed URLs for a batch of private-bucket paths.
async function signPaths(paths: string[]): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  try {
    const res = await fetch('/api/photos/signed-urls', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paths }),
    });
    if (res.ok) return (await res.json())?.urls || {};
  } catch { /* ignore */ }
  return {};
}

export default function InspectionBuilder({ inspectionId, pipelineId, customer, onClose }: InspectionBuilderProps) {
  const supabase = createClient();

  const [title, setTitle] = useState('Roof Inspection');
  const [inspectorName, setInspectorName] = useState('');
  const [inspectionDate, setInspectionDate] = useState('');
  const [coverNote, setCoverNote] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Markup editor target
  const [annotatingId, setAnnotatingId] = useState<string | null>(null);

  // Export / email
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  // Photo picker (this customer's field photos not yet added)
  const [showPicker, setShowPicker] = useState(false);
  const [fieldPhotos, setFieldPhotos] = useState<{ id: string; path: string; src: string }[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  // Load inspection + items
  const loadInspection = useCallback(async () => {
    setLoading(true);
    try {
      const [insRes, itemRes] = await Promise.all([
        supabase.from('inspections').select('*').eq('id', inspectionId),
        supabase.from('inspection_items').select('*').eq('inspection_id', inspectionId).order('sort_order', { ascending: true }),
      ]);
      const ins = insRes.data?.[0];
      if (ins) {
        setTitle(ins.title || 'Roof Inspection');
        setInspectorName(ins.inspector_name || '');
        setInspectionDate(ins.inspection_date || '');
        setCoverNote(ins.cover_note || '');
      }
      const rows = itemRes.data || [];
      const paths = rows.map((r: any) => r.storage_path).filter(Boolean);
      const urlMap = await signPaths(paths);
      setItems(
        rows.map((r: any) => ({
          id: r.id,
          storage_path: r.storage_path,
          source_photo_id: r.source_photo_id,
          note: r.note || '',
          annotations: Array.isArray(r.annotations) ? r.annotations : [],
          sort_order: r.sort_order ?? 0,
          src: r.storage_path ? urlMap[r.storage_path] || '' : '',
        })),
      );
    } catch (e: any) {
      setError(e?.message || 'Failed to load inspection.');
    } finally {
      setLoading(false);
    }
  }, [inspectionId, supabase]);

  useEffect(() => { loadInspection(); }, [loadInspection]);

  // Load available field photos when opening the picker
  const openPicker = async () => {
    setShowPicker(true);
    setLoadingPhotos(true);
    try {
      const { data } = await supabase
        .from('photos')
        .select('id, storage_path')
        .eq('pipeline_id', pipelineId)
        .eq('is_archived', false)
        .order('taken_at', { ascending: false });
      const rows = (data || []).filter((r: any) => r.storage_path);
      const urlMap = await signPaths(rows.map((r: any) => r.storage_path));
      setFieldPhotos(rows.map((r: any) => ({ id: r.id, path: r.storage_path, src: urlMap[r.storage_path] || '' })));
    } catch {
      setFieldPhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const addPhoto = async (photo: { id: string; path: string; src: string }) => {
    const sort_order = items.length;
    try {
      const { data, error: err } = await supabase
        .from('inspection_items')
        .insert({ inspection_id: inspectionId, storage_path: photo.path, source_photo_id: photo.id, note: '', sort_order })
        .select();
      if (err) throw err;
      const row = data?.[0];
      setItems(prev => [...prev, {
        id: row?.id, storage_path: photo.path, source_photo_id: photo.id, note: '', annotations: [], sort_order, src: photo.src,
      }]);
    } catch (e: any) {
      setError(e?.message || 'Could not add photo.');
    }
  };

  const removeItem = async (id: string) => {
    try {
      await supabase.from('inspection_items').delete().eq('id', id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch { /* ignore */ }
  };

  const setNote = (id: string, note: string) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, note } : i)));
  };

  const saveAnnotations = async (itemId: string, annotations: Annotation[]) => {
    setItems(prev => prev.map(i => (i.id === itemId ? { ...i, annotations } : i)));
    setAnnotatingId(null);
    try {
      await supabase
        .from('inspection_items')
        .update({ annotations, updated_at: new Date().toISOString() })
        .eq('id', itemId);
    } catch (e: any) {
      setError(e?.message || 'Could not save markup.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: insErr } = await supabase
        .from('inspections')
        .update({
          title: title.trim() || 'Roof Inspection',
          inspector_name: inspectorName.trim() || null,
          inspection_date: inspectionDate || null,
          cover_note: coverNote.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', inspectionId);
      if (insErr) throw insErr;

      // Persist notes + order for each item.
      for (let idx = 0; idx < items.length; idx++) {
        const it = items[idx];
        const { error: itErr } = await supabase
          .from('inspection_items')
          .update({ note: it.note, sort_order: idx, updated_at: new Date().toISOString() })
          .eq('id', it.id);
        if (itErr) throw itErr;
      }
      setSavedAt(new Date().toLocaleTimeString('en-US'));
    } catch (e: any) {
      setError(e?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  // Measure a photo's aspect ratio so PDF boxes preserve it (keeps annotations aligned).
  const measureAspect = (url: string) => new Promise<number>(resolve => {
    const img = new window.Image();
    img.onload = () => resolve(img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 4 / 3);
    img.onerror = () => resolve(4 / 3);
    img.src = url;
  });

  const blobToBase64 = (blob: Blob) => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result); resolve(s.slice(s.indexOf(',') + 1)); };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

  const buildPdfData = async (): Promise<InspectionPdfData> => {
    await handleSave(); // persist notes/order first
    const withPhotos = items.filter(i => i.src);
    const pdfItems: InspectionPdfItem[] = await Promise.all(withPhotos.map(async it => ({
      src: it.src!,
      note: it.note,
      annotations: (it.annotations || []) as any,
      aspect: await measureAspect(it.src!),
    })));
    return {
      title: title.trim() || 'Roof Inspection',
      date: inspectionDate ? new Date(inspectionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
      inspectorName: inspectorName.trim() || undefined,
      coverNote: coverNote.trim() || undefined,
      logoSrc: `${window.location.origin}/assets/images/nextroof-logo.png`,
      company: { name: 'NextRoof 360', email: 'info@getnextroof.com' },
      customer,
      items: pdfItems,
    };
  };

  const fileName = () => `${(title || 'inspection').replace(/\s+/g, '-').toLowerCase()}.pdf`;

  const handleDownload = async () => {
    setPdfBusy(true);
    setError(null);
    try {
      const data = await buildPdfData();
      const { downloadInspectionPdf } = await import('@/components/common/InspectionPDF');
      await downloadInspectionPdf(data, fileName());
    } catch (e: any) {
      setError(e?.message || 'Could not generate the PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  const openEmail = () => {
    setEmailTo(customer.email || '');
    setEmailSubject(`${title || 'Roof Inspection'} — ${customer.name}`);
    setEmailMessage('Please find your roof inspection report attached.');
    setEmailError(null);
    setEmailSent(false);
    setShowEmail(true);
  };

  const handleEmailSend = async () => {
    setEmailBusy(true);
    setEmailError(null);
    try {
      const data = await buildPdfData();
      const { buildInspectionPdfBlob } = await import('@/components/common/InspectionPDF');
      const blob = await buildInspectionPdfBlob(data);
      const b64 = await blobToBase64(blob);
      const res = await fetch('/api/inspections/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailTo.trim(),
          subject: emailSubject.trim() || 'Your Inspection Report',
          message: emailMessage.trim(),
          filename: fileName(),
          pdfBase64: b64,
          inspectionId,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || 'Email failed to send.');
      setEmailSent(true);
      setTimeout(() => { setShowEmail(false); setEmailSent(false); }, 1600);
    } catch (e: any) {
      setEmailError(e?.message || 'Email failed to send.');
    } finally {
      setEmailBusy(false);
    }
  };

  const custLine = [customer.address, [customer.city, customer.state, customer.zip].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
  const alreadyAdded = new Set(items.map(i => i.storage_path));

  // Group items into pages of 3 (matches the PDF layout).
  const pages: Item[][] = [];
  for (let i = 0; i < items.length; i += 3) pages.push(items.slice(i, i + 3));

  return (
    <div className="fixed inset-0 z-[130] bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-between gap-2 px-4 md:px-6 py-3 bg-white border-b border-gray-200 overflow-x-auto">
        <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 flex-shrink-0" aria-label="Close">
            <Icon name="ArrowLeftIcon" size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="text-base font-bold leading-tight" style={{ color: NAVY }}>Inspection Report</h1>
            <p className="text-xs text-gray-400 truncate">{customer.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {savedAt && <span className="hidden sm:inline text-xs text-gray-400">Saved {savedAt}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 whitespace-nowrap"
            style={{ backgroundColor: NAVY }}
          >
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="CheckIcon" size={15} variant="solid" />}
            <span className="hidden sm:inline">Save</span>
          </button>
          <button
            onClick={handleDownload}
            disabled={pdfBusy || items.length === 0}
            title="Download PDF"
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            {pdfBusy ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> : <Icon name="ArrowDownTrayIcon" size={15} />}
            <span className="hidden sm:inline">Download PDF</span>
          </button>
          <button
            onClick={openEmail}
            disabled={items.length === 0}
            title="Email to customer"
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 whitespace-nowrap"
            style={{ backgroundColor: ORANGE }}
          >
            <Icon name="PaperAirplaneIcon" size={15} />
            <span className="hidden sm:inline">Email</span>
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {error && (
          <div className="max-w-4xl mx-auto mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-5">
            {/* Cover sheet */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2" style={{ background: NAVY }}>
                <Icon name="ClipboardDocumentCheckIcon" size={17} className="text-white" />
                <h2 className="text-sm font-bold text-white">Cover Page</h2>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Report Title</label>
                    <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Inspection Date</label>
                    <input type="date" value={inspectionDate} onChange={e => setInspectionDate(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Inspector</label>
                    <input value={inspectorName} onChange={e => setInspectorName(e.target.value)} placeholder="Inspector name" className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Prepared For</label>
                    <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-700">
                      <p className="font-medium">{customer.name}</p>
                      {custLine && <p className="text-xs text-gray-500">{custLine}</p>}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Summary / Cover Note</label>
                  <textarea value={coverNote} onChange={e => setCoverNote(e.target.value)} rows={3} placeholder="Overview of the inspection findings…" className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40 resize-y" />
                </div>
              </div>
            </div>

            {/* Add photos */}
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-600">
                {items.length} photo{items.length === 1 ? '' : 's'} · {pages.length} page{pages.length === 1 ? '' : 's'} (3 per page)
              </p>
              <button onClick={openPicker} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: ORANGE }}>
                <Icon name="PlusIcon" size={15} variant="solid" /> Add Photos
              </button>
            </div>

            {items.length === 0 ? (
              <button onClick={openPicker} className="w-full flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-300 rounded-2xl bg-white hover:border-blue-300 transition-colors">
                <Icon name="PhotoIcon" size={34} className="text-gray-300 mb-2" />
                <p className="text-sm font-medium text-gray-500">Add field photos to build the report</p>
                <p className="text-xs text-gray-400 mt-0.5">Each photo gets a note; 3 photos per printed page.</p>
              </button>
            ) : (
              pages.map((pageItems, pIdx) => (
                <div key={pIdx} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-2 border-b border-gray-100 bg-gray-50">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Page {pIdx + 1}</p>
                  </div>
                  <div className="p-5 space-y-5">
                    {pageItems.map(it => (
                      <div key={it.id} className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-start">
                        {/* Note (left) */}
                        <div className="sm:col-span-2 order-2 sm:order-1">
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notes</label>
                          <textarea
                            value={it.note}
                            onChange={e => setNote(it.id, e.target.value)}
                            rows={5}
                            placeholder="Describe what this photo shows…"
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40 resize-y"
                          />
                        </div>
                        {/* Photo (right) */}
                        <div className="sm:col-span-3 order-1 sm:order-2">
                          <div className="relative rounded-lg overflow-hidden bg-gray-100 border border-gray-200 aspect-[4/3]">
                            {it.src ? (
                              <AppImage src={it.src} alt="Inspection photo" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-300"><Icon name="PhotoIcon" size={28} /></div>
                            )}
                            <div className="absolute top-2 right-2 flex items-center gap-1.5">
                              <button
                                onClick={() => setAnnotatingId(it.id)}
                                disabled={!it.src}
                                title="Add arrows, circles, and labels"
                                className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-black/60 text-white hover:bg-black/80 disabled:opacity-40"
                              >
                                <Icon name="PencilIcon" size={11} /> Markup
                              </button>
                              <button
                                onClick={() => removeItem(it.id)}
                                className="p-1 rounded-md bg-black/50 text-white hover:bg-red-600"
                                aria-label="Remove photo"
                              >
                                <Icon name="TrashIcon" size={12} />
                              </button>
                            </div>
                            {it.annotations.length > 0 && (
                              <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px]">
                                {it.annotations.length} markup{it.annotations.length === 1 ? '' : 's'}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Photo picker modal */}
      {showPicker && (
        <div className="fixed inset-0 z-[140] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPicker(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold" style={{ color: NAVY }}>Add field photos</h3>
              <button onClick={() => setShowPicker(false)} className="text-gray-400 hover:text-gray-700"><Icon name="XMarkIcon" size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loadingPhotos ? (
                <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
              ) : fieldPhotos.length === 0 ? (
                <div className="text-center py-12 text-sm text-gray-400">
                  No field photos for this customer yet. Take/upload photos on the Photos tab first.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {fieldPhotos.map(p => {
                    const added = alreadyAdded.has(p.path);
                    return (
                      <button
                        key={p.id}
                        onClick={() => !added && addPhoto(p)}
                        disabled={added}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 ${added ? 'border-green-500 opacity-60' : 'border-transparent hover:border-blue-400'}`}
                      >
                        <AppImage src={p.src} alt="Field photo" className="w-full h-full object-cover" />
                        {added && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <span className="bg-green-500 text-white rounded-full p-1"><Icon name="CheckIcon" size={14} variant="solid" /></span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
              <button onClick={() => setShowPicker(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: NAVY }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Email modal */}
      {showEmail && (
        <div className="fixed inset-0 z-[145] bg-black/50 flex items-center justify-center p-4" onClick={() => !emailBusy && setShowEmail(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold" style={{ color: NAVY }}>Email inspection report</h3>
              <button onClick={() => setShowEmail(false)} className="text-gray-400 hover:text-gray-700"><Icon name="XMarkIcon" size={18} /></button>
            </div>
            {emailSent ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Icon name="CheckIcon" size={24} variant="solid" className="text-green-600" />
                </div>
                <p className="text-sm font-semibold text-gray-800">Sent to {emailTo}</p>
              </div>
            ) : (
              <div className="p-5 space-y-3">
                {emailError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">{emailError}</div>}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">To</label>
                  <input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="customer@email.com" className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject</label>
                  <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message</label>
                  <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40 resize-y" />
                </div>
                <p className="text-xs text-gray-400">The report PDF ({items.length} photo{items.length === 1 ? '' : 's'}) will be attached.</p>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setShowEmail(false)} disabled={emailBusy} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleEmailSend} disabled={emailBusy} className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2" style={{ backgroundColor: NAVY }}>
                    {emailBusy ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="PaperAirplaneIcon" size={15} />}
                    Send
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo markup editor */}
      {annotatingId && (() => {
        const it = items.find(i => i.id === annotatingId);
        if (!it || !it.src) return null;
        return (
          <PhotoAnnotator
            src={it.src}
            initial={it.annotations as Annotation[]}
            onCancel={() => setAnnotatingId(null)}
            onSave={anns => saveAnnotations(annotatingId, anns)}
          />
        );
      })()}
    </div>
  );
}
